import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, Video, Youtube, BookOpen, Loader, Loader2, Download, Volume2, FileText, PlayCircle, AlertTriangle, Eye, EyeOff, Plus, List, CheckCircle, Save, Info, X, ChevronLeft, CloudUpload, CheckCircle2, Play, FileVideo, FolderOpen, Trash2, FileSpreadsheet, Cloud, Folder, Calendar, ExternalLink, RefreshCw, Sparkles, Settings2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ConfirmDialog } from './design-system';
import { useMsalAuth } from './MsalProvider';
import OneDriveService from '../utils/oneDriveService';
import OneDrivePicker from './OneDrivePicker';
import PitchAnalytics from './PitchAnalytics';
import { analyzeVideo, generateAudio, generatePDF, analyzeMultipleVideos } from '../utils/videoAnalyticsApi';
import { saveVideoAnalytics, getAllVideoAnalytics, deleteVideoAnalytics } from '../utils/videoAnalyticsApi';
import { getAthleticPowerMetricsByPlayerId } from '../utils/athleticPowerApi';
import VideoModal from './VideoModal';
import { fetchVideoAnalysis, formatFileSize, getSelectedSport, successMessage, fetchVideoCsv, getUser, errorMessage, getAuthToken } from '../utils/helpers';
import VideoPlayer from './VideoPlayer';
import axios from 'axios';
import apiClient from '../utils/apiClient';
import { NODE_BACKEND_URL } from '../utils/constant';
import VideoAnalyticsTable from './VideoAnalyticsTable';
import FolderDropdown from './FolderDropdown';
import { getMBBRoasters } from '../utils/MBBApis';
import { VideoAnalyzer, CompactClassicAnalysis } from './v2/analyzer';
import YouTubeErrorDisplay, { isYouTubeError, YouTubeRestrictionsTooltip } from './YouTubeErrorDisplay';
import useVideoAnalysisNotifications from '../hooks/useVideoAnalysisNotifications';
import { useSportContext } from '../hooks/useSportContext';
import { C } from './design-system/primitives';

const VideoAnalytics = () => {
  const isDarkMode = true; // OLED: always dark
  // Sport context - reactive to Settings changes
  const {
    sportCode,
    sportName,
    teamId,
    teamName,
    isBaseball,
    isBasketball,
    hasSelection: hasSportSelection,
  } = useSportContext();

  // Analysis tab: 'analysis' or 'myvideos' (simplified from 3 tabs to 2)
  const [analysisTab, setAnalysisTab] = useState('analysis'); // Default to video analysis
  // Legacy view mode - deprecated, kept for compatibility
  const [useLegacyClassic, setUseLegacyClassic] = useState(false);
  const [inputType, setInputType] = useState('upload');
  // My Videos section states
  const [dragActive, setDragActive] = useState(false);
  const [myVideos, setMyVideos] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [myVideoFile, setMyVideoFile] = useState(null);
  const [myVideosErrorMessage, setMyVideosErrorMessage] = useState('');
  // const [loadingMyVideos, setLoadingMyVideos] = useState(false);
  // const [showMyVideosViewer, setShowMyVideosViewer] = useState(false);
  // const [viewerData, setViewerData] = useState(null);
  // const [selectedFileInfo, setSelectedFileInfo] = useState(null);
  // const [myVideosCurrentPage, setMyVideosCurrentPage] = useState(1);
  // const [myVideosPageSize, setMyVideosPageSize] = useState(10);
  // const [myVideosTotalRecords, setMyVideosTotalRecords] = useState(0);
  const { instance, isAuthenticated, isInitialized, clearAuthState, forceNewLogin } = useMsalAuth();
  const [oneDriveService, setOneDriveService] = useState(null);

  // Initialize OneDrive service only when needed and authenticated
  useEffect(() => {
    if (instance && isInitialized && isAuthenticated) {
      try {
        console.log('Creating OneDriveService in VideoAnalytics');
        const service = new OneDriveService(instance);
        setOneDriveService(service);
      } catch (error) {
        console.error('Failed to create OneDriveService:', error);
      }
    }
  }, [instance, isInitialized, isAuthenticated]);

  // State for tracking OneDrive downloads
  const [oneDriveDownloads, setOneDriveDownloads] = useState({});
  const [isDownloadingOneDriveFiles, setIsDownloadingOneDriveFiles] = useState(false);
  const [videoFiles, setVideoFiles] = useState([]);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [additionalInstructions, setAdditionalInstructions] = useState('Analyze this video');
  const [analysisResult, setAnalysisResult] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isDownloadingYoutube, setIsDownloadingYoutube] = useState(false);
  const [actionsVisible, setActionsVisible] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  // const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [narrationText, setNarrationText] = useState(null);
  const [showNarrationText, setShowNarrationText] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  // const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [videoPreviews, setVideoPreviews] = useState([]);
  const [playerIdForAnalysis, setPlayerIdForAnalysis] = useState('');
  // const [savedAnalytics, setSavedAnalytics] = useState([]);
  // const [showSavedAnalytics, setShowSavedAnalytics] = useState(false);
  // const [isSaving, setIsSaving] = useState(false);
  const [analysisTitle, setAnalysisTitle] = useState('');
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);
  const [athleticPowerMetrics, setAthleticPowerMetrics] = useState(null);
  const [showAthleticPowerMetrics, setShowAthleticPowerMetrics] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [existingVideos, setExistingVideos] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  // const [videoAnalytics, setVideoAnalytics] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [dropdownFolderId, setDropdownFolderId] = useState(null);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [allFolders, setAllFolders] = useState([])
  const [isFolderLoading, setIsFolderLoading] = useState(false)
  const [isTableLoading, setIsTableLoading] = useState(false);
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search);
  const [isShowCsv, serIsShowCsv] = useState(false)
  const folderId = searchParams.get('folder');

  // Video analysis notifications - polls for status changes and shows notifications
  const { checkNow: checkVideoStatuses } = useVideoAnalysisNotifications({
    enabled: true,
    pollInterval: 30000, // 30 seconds
    folderId: selectedFolderId || folderId,
    onStatusChange: (video, status) => {
      // Refresh the video list when any video completes/fails
      fetchExistingVideos();
    },
  });

  // Fetch saved analytics on component mount
  useEffect(() => {
    fetchExistingVideos()
    fetchPlayers();
    fetchAllFolders()
    if (folderId) {
      setSelectedFolderId(folderId);
    }

    // Set player ID from URL if available
    const player_id = searchParams.get('player_id');
    if (player_id) {
      setPlayerIdForAnalysis(player_id);
    }

    // Check for tab parameter to switch tabs
    const tabParam = searchParams.get('tab');
    if (tabParam === 'myvideos') {
      setAnalysisTab('myvideos');
    }
    // Support legacy 'moe' tab param - redirect to unified 'analysis' tab
    if (tabParam === 'moe') {
      setAnalysisTab('analysis');
    }
  }, [location.search]);
  
  // Update playerIdForAnalysis when allPlayers updates and player_id exists in URL
  useEffect(() => {
    if (allPlayers.length > 0) {
      const player_id = searchParams.get('player_id');
      if (player_id) {
        // Check if player_id exists in allPlayers
        const playerExists = allPlayers.some(player => 
          player.player_name === player_id || player.name === player_id
        );
        if (playerExists) {
          setPlayerIdForAnalysis(player_id);
        }
      }
    }
  }, [allPlayers, searchParams]);

  useEffect(() => {
    fetchExistingVideos()
  }, [selectedFolderId]);

  // Re-fetch data when sport changes from Settings
  useEffect(() => {
    if (sportCode) {
      console.log('[VideoAnalytics] Sport changed to:', sportCode, '- refreshing data');
      fetchExistingVideos();
      fetchPlayers();
    }
  }, [sportCode]);

  // Get OneDrive token ONLY when already authenticated (not triggering login)
  useEffect(() => {
    // Only proceed if user is already authenticated and MSAL is initialized
    if (isAuthenticated && instance && isInitialized && oneDriveService) {
      try {
        console.log('Attempting to get OneDrive token silently');
        // Only try to get a token silently (no popups)
        oneDriveService.getTokenSilently()
          .then(token => {
            if (token) {
              sessionStorage.setItem('oneDriveToken', token);
              console.log('OneDrive token saved successfully');
            } else {
              // If no token returned but no error thrown, clear auth state
              console.warn('No token returned from silent authentication');
              clearAuthState();
            }
          })
          .catch(error => {
            // Log the error but don't show popup
            console.error('Silent token acquisition failed:', error);

            // If the error is related to no active account, clear auth state
            if (error.message && (error.message.includes('No active account') ||
              error.message.includes('not initialized') ||
              error.message.includes('login required'))) {
              console.log('Authentication issue detected, clearing auth state');
              clearAuthState();
            }
          });
      } catch (error) {
        console.error('Error in OneDrive silent authentication:', error);
        // Clear auth state if there's a fatal error
        clearAuthState();
      }
    }
  }, [isAuthenticated, instance, isInitialized, oneDriveService, clearAuthState]);

  const fetchAllFolders = async () => {
    setIsFolderLoading(true);
    try {
      // Use axios directly with NODE_BACKEND_URL (not apiClient which uses gateway)
      const response = await axios.get(`${NODE_BACKEND_URL}/api/get-folders?user_id=${getUser()?.id}`, {
        headers: { Authorization: getAuthToken() }
      });
      setAllFolders(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching folders:', error);
      // If the API fails, provide some default folders
      setAllFolders([]);
    } finally {
      setIsFolderLoading(false);
    }
  };

  const handleFolderClick = (folderId, folderName) => {
    setSelectedFolderId(folderId);
    setDropdownFolderId(folderId)
    // Update URL with folder ID
    navigate(`?folder=${folderId}`);
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      // Use axios directly with NODE_BACKEND_URL (not apiClient which uses gateway)
      const response = await axios.post(`${NODE_BACKEND_URL}/api/delete-folder`, {
        id: folderId,
        user_id: getUser()?.id
      }, {
        headers: { Authorization: getAuthToken() }
      });

      if (response.data?.success) {
        successMessage('Folder deleted successfully');
        // Remove folder from state
        setAllFolders(prev => prev.filter(folder => folder.id !== folderId));

        // If the current selected folder is deleted, reset selection
        if (selectedFolderId === folderId) {
          setSelectedFolderId(null);
          navigate('/video-analytics'); // Remove folder from URL
        }
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
    }
  };

  const fetchExistingVideos = async (page = 1, size = 10, overrideFolderId) => {
    setIsTableLoading(true);
    try {
      const folderToUse = overrideFolderId !== undefined ? overrideFolderId : selectedFolderId;
      const videos = await fetchVideoAnalysis(null, page, size, folderToUse);
      // console.log('videos',videos)
      setExistingVideos(videos?.videos);
      setTotalRecords(videos?.total);
    } catch (error) {
      console.error('Error fetching existing videos:', error);
    } finally {
      setIsTableLoading(false);
    }
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
    fetchExistingVideos(page, size)
  };

  // Load athletic power metrics for a Player
  const loadAthleticPowerMetrics = async (playerId) => {
    if (!playerId) {
      setAthleticPowerMetrics(null);
      setShowAthleticPowerMetrics(false);
      return;
    }

    setLoadingMetrics(true);

    try {
      const metrics = await getAthleticPowerMetricsByPlayerId(playerId);
      setAthleticPowerMetrics(metrics);
      setShowAthleticPowerMetrics(true);
    } catch (error) {
      console.error('Error loading athletic power metrics:', error);
      setAthleticPowerMetrics(null);
      setShowAthleticPowerMetrics(false);
    } finally {
      setLoadingMetrics(false);
    }
  };

  // Handle video analysis
  const handleAnalyzeVideo = async () => {
    if (!dropdownFolderId?.length) {
      return errorMessage('Please select a folder before uploading a video')
    }
    // Reset states
    setIsAnalyzing(true);
    setIsDownloadingYoutube(inputType === 'youtube');
    setAnalysisResult('');
    setActionsVisible(false);
    setAudioUrl(null);
    setPdfUrl(null);
    setError(null);
    setAthleticPowerMetrics(null);
    setShowAthleticPowerMetrics(false);

    try {
      // Validate inputs
      if (inputType === 'upload' && videoFiles.length === 0) {
        throw new Error('Please upload at least one video file');
      }
      if (inputType === 'youtube' && !youtubeUrl) {
        throw new Error('Please enter a valid YouTube URL');
      }

      // Validate YouTube URL format if that's the input type
      if (inputType === 'youtube') {
        // Check basic YouTube URL format
        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
        if (!youtubeRegex.test(youtubeUrl)) {
          throw new Error('Please enter a valid YouTube URL (e.g., https://www.youtube.com/watch?v=...)');
        }

        // For youtube.com links, ensure it has a video ID
        if (youtubeUrl.includes('youtube.com')) {
          const isWatchUrl = youtubeUrl.includes('/watch?v=');
          const isShortsUrl = youtubeUrl.includes('/shorts/');
        
          if (!isWatchUrl && !isShortsUrl) {
            throw new Error(
              'Please provide a valid YouTube watch or shorts URL (e.g., https://www.youtube.com/watch?v=videoId or https://www.youtube.com/shorts/videoId)'
            );
          }
        }

        // For youtu.be links, ensure it has a video ID
        if (youtubeUrl.includes('youtu.be') && youtubeUrl.split('/').pop() === '') {
          throw new Error('Please provide a valid shortened YouTube URL (e.g., https://youtu.be/videoId)');
        }

        // Ensure URL is properly formatted for Gemini API
        if (!youtubeUrl.startsWith('http')) {
          // Add https:// if not present
          setYoutubeUrl(`https://${youtubeUrl}`);
        }
      }

      // Check if we have a CSV file to upload first
      let csvId = null;
      let csvName = null;
      const csvFile = myVideos.find(file => file.type === 'csv');

      if (csvFile) {
        try {
          successMessage(`Uploading CSV file: ${csvFile.name}...`);

          // Create a FormData instance for the CSV upload
          const csvFormData = new FormData();

          // Handle different file types (OneDrive vs regular upload)
          let csvFileToUpload;

          // Check if CSV is from OneDrive
          if (csvFile.isOneDrive) {
            successMessage(`Downloading CSV from OneDrive: ${csvFile.name}...`);
            const blob = await downloadOneDriveFile(csvFile.file.oneDriveId);
            csvFileToUpload = new File([blob], csvFile.name, { type: 'text/csv' });
          } else {
            csvFileToUpload = csvFile.file;
          }

          csvFormData.append('file', csvFileToUpload);

          // Upload the CSV file first
          const csvRes = await apiClient.post(
            `https://slam-all-python-359065791766.us-central1.run.app/${getSelectedSport()}/video-analysis/upload-csv`,
            csvFormData
          );

          const csvData = csvRes?.data;
          if (!csvData?.success) {
            throw new Error(`CSV upload failed for ${csvFile.name}`);
          }

          // Store the CSV ID for video analysis
          csvId = csvData.csv_id;
          csvName = csvFile.name;

          // Update the CSV file in myVideos with the csv_id
          setMyVideos(prevFiles =>
            prevFiles.map(file =>
              file.id === csvFile.id ? { ...file, csv_id: csvId } : file
            )
          );

          successMessage(`CSV file uploaded successfully: ${csvFile.name}`);
        } catch (csvError) {
          console.error(`Error uploading CSV ${csvFile.name}:`, csvError);
          setError(`CSV upload error: ${csvError.message}`);
          setIsAnalyzing(false);
          return;
        }
      }

      // Call the API
      let result;
      if (inputType === 'upload') {
        if (videoFiles.length > 1) {
          // Use multiple video analysis with CSV ID if available
          result = await analyzeMultipleVideos({
            videoFiles,
            instructions: additionalInstructions,
            player_id: playerIdForAnalysis,
            analysisTitle: analysisTitle,
            folderId: dropdownFolderId,
            csvId: csvId,
            csvName: csvName
          });
          // Join results with a separator
          // setAnalysisResult(result.join('\n\n--- Next Video Analysis ---\n\n'));
        } else {
          // Use single video analysis with CSV ID if available
          result = await analyzeVideo({
            videoFile: videoFiles[0],
            youtubeUrl: null,
            instructions: additionalInstructions,
            player_id: playerIdForAnalysis,
            analysisTitle: analysisTitle,
            folderId: dropdownFolderId,
            csvId: csvId,
            csvName: csvName
          });
          // setAnalysisResult(result);
        }
      } else {
        // YouTube analysis with CSV ID if available
        result = await analyzeVideo({
          videoFile: null,
          youtubeUrl: youtubeUrl,
          instructions: additionalInstructions,
          player_id: playerIdForAnalysis,
          analysisTitle: analysisTitle,
          folderId: dropdownFolderId,
          csvId: csvId,
          csvName: csvName
        });
        // setAnalysisResult(result);
      }
      setVideoFiles([])
      setIsAnalyzing(false);
      successMessage('Upload started. Your videos are being processed.');

      // Store any CSV files with their CSV IDs before resetting form
      const csvFilesWithIds = myVideos.filter(file => file.type === 'csv' && file.csv_id);

      // Reset the form (keeps CSV files intact in myVideos array)
      resetForm();

      // Update UI
      fetchAllFolders();
      setMyVideos([])
      successMessage('Upload complete. Your videos are now being processed.');
      setActionsVisible(true);

      // Navigate to the folder view and switch to My Videos tab to show results
      const targetFolderId = selectedFolderId || dropdownFolderId;
      if (targetFolderId) {
        setSelectedFolderId(targetFolderId);
        navigate(`?folder=${targetFolderId}`);
        setAnalysisTab('myvideos'); // Switch to tab where results live
        // Explicit refresh: handles race condition + case where selectedFolderId unchanged (no useEffect re-fire)
        fetchExistingVideos(1, pageSize, targetFolderId);
      }
    } catch (error) {
      console.error('Analysis error:', error);

      // Handle specific error messages for YouTube
      if (inputType === 'youtube' && error.message) {
        if (error.message.includes('timeout') || error.message.includes('timed out')) {
          setError('The YouTube video processing timed out. Please try a shorter video (under 5 minutes).');
        } else if (error.message.includes('video is too long')) {
          setError('The YouTube video is too long. Please use videos under 10 minutes for best results.');
        } else if (error.message.includes('private') || error.message.includes('unavailable')) {
          setError('This YouTube video is private or unavailable. Please try a different video.');
        } else if (error.message.includes('Could not extract') || error.message.includes('download')) {
          setError('Could not download YouTube video due to YouTube restrictions. Try a different video or upload a file directly.');
        } else {
          setError(`YouTube processing error: ${error.message}`);
        }
      } else {
        setError(error.message || 'An unexpected error occurred');
      }

      setAnalysisResult('');
    } finally {
      setIsAnalyzing(false);
      setIsDownloadingYoutube(false);
    }
  };

  // Handle file upload with drag and drop support
  const handleFileChange = (e) => {
    // Declare variables outside try block so they're accessible in catch
    let validVideoFiles = [];
    let validCsvFiles = [];
    let validPreviews = [];
    let errors = [];

    try {
      // Clear any existing errors
      setError(null);

      // Check if files were actually selected
      if (!e.target.files || e.target.files.length === 0) {
        setError('No files selected. Please choose at least one file.');
        return;
      }

      const files = Array.from(e.target.files);

      // Configuration constants
      const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
      const MAX_VIDEO_FILES = 50; // Adjust as needed
      const SUPPORTED_VIDEO_TYPES = ['video/mp4', 'video/avi', 'video/mov', 'video/quicktime'];
      const SUPPORTED_VIDEO_EXTENSIONS = ['mp4', 'avi', 'mov'];
      const SUPPORTED_CSV_TYPES = ['text/csv', 'application/csv'];

      // Check if we already have a CSV file
      const existingCsvFile = myVideos.find(file => file.type === 'csv');

      // Process each file
      files.forEach((file, index) => {
        try {
          // Check if file object is valid
          if (!file || !file.name || file.size === undefined) {
            errors.push(`File ${index + 1}: Invalid file object.`);
            return;
          }

          // Check file size
          if (file.size === 0) {
            errors.push(`"${file.name}": File is empty.`);
            return;
          }

          if (file.size > MAX_FILE_SIZE) {
            errors.push(`"${file.name}": File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds maximum limit of ${MAX_FILE_SIZE / 1024 / 1024}MB.`);
            return;
          }

          // Get file extension safely
          const fileName = file.name.trim();
          if (!fileName || fileName.length === 0) {
            errors.push(`File ${index + 1}: Invalid file name.`);
            return;
          }

          const nameParts = fileName.split('.');
          if (nameParts.length < 2) {
            errors.push(`"${fileName}": File has no extension.`);
            return;
          }

          const fileExtension = nameParts.pop().toLowerCase();
          if (!fileExtension) {
            errors.push(`"${fileName}": Invalid file extension.`);
            return;
          }

          // Check for potentially dangerous file names
          if (fileName.includes('..') || fileName.includes('/') || fileName.includes('\\')) {
            errors.push(`"${fileName}": Invalid characters in file name.`);
            return;
          }

          // Handle video files
          if (file.type.startsWith('video/') || SUPPORTED_VIDEO_EXTENSIONS.includes(fileExtension)) {

            // Verify video MIME type and extension
            const isValidType = SUPPORTED_VIDEO_TYPES.includes(file.type);
            const isValidExtension = SUPPORTED_VIDEO_EXTENSIONS.includes(fileExtension);

            if (!isValidType && !isValidExtension) {
              errors.push(`"${fileName}": Unsupported video format. Only MP4, AVI, and MOV files are supported.`);
              return;
            }

            // Warn if MIME type doesn't match extension (but still allow it)
            if (isValidExtension && !isValidType && file.type !== '') {
              console.warn(`Video file "${fileName}" has unexpected MIME type: ${file.type}`);
            }

            // Check if we're exceeding video file limit
            const currentVideoCount = videoFiles ? videoFiles.length : 0;
            if (currentVideoCount + validVideoFiles.length >= MAX_VIDEO_FILES) {
              errors.push(`Maximum of ${MAX_VIDEO_FILES} video files allowed. "${fileName}" was skipped.`);
              return;
            }

            // Check for duplicate video file names
            const isDuplicateInCurrent = validVideoFiles.some(existingFile => existingFile.name === fileName);
            const isDuplicateInExisting = videoFiles && videoFiles.some(existingFile => existingFile.name === fileName);

            if (isDuplicateInCurrent || isDuplicateInExisting) {
              errors.push(`"${fileName}": A video file with this name already exists.`);
              return;
            }

            validVideoFiles.push(file);

            // Create preview URL safely
            try {
              const previewUrl = URL.createObjectURL(file);
              validPreviews.push(previewUrl);
            } catch (urlError) {
              console.error('Failed to create preview URL for', fileName, urlError);
              errors.push(`"${fileName}": Failed to create preview.`);
              return;
            }
          }
          // Handle CSV files
          else if (fileExtension === 'csv' || SUPPORTED_CSV_TYPES.includes(file.type)) {

            // Check if we already have a CSV file
            if (existingCsvFile) {
              errors.push('Only one CSV file is allowed. Please remove the existing CSV file first.');
              return;
            }

            // Check if we already processed a CSV in this upload
            if (validCsvFiles.length > 0) {
              errors.push(`Only one CSV file is allowed. "${fileName}" was skipped.`);
              return;
            }

            // Check for duplicate CSV file name
            const isDuplicateCsv = myVideos.some(existingFile =>
              existingFile.name === fileName && existingFile.type === 'csv'
            );
            if (isDuplicateCsv) {
              errors.push(`"${fileName}": A CSV file with this name already exists.`);
              return;
            }

            // CSV file size limit (10MB for CSV files)
            const CSV_MAX_SIZE = 10 * 1024 * 1024;
            if (file.size > CSV_MAX_SIZE) {
              errors.push(`"${fileName}": CSV file too large. Maximum size is ${CSV_MAX_SIZE / 1024 / 1024}MB.`);
              return;
            }

            validCsvFiles.push(file);
          }
          // Handle unsupported files
          else {
            errors.push(`"${fileName}": Unsupported file type. Only video files (MP4, AVI, MOV) and CSV files are accepted.`);
            return;
          }
        } catch (fileError) {
          console.error('Error processing file:', file?.name || 'Unknown', fileError);
          errors.push(`"${file?.name || 'Unknown file'}": Error processing file - ${fileError.message}`);
        }
      });

      // Check if any valid files were processed
      if (validVideoFiles.length === 0 && validCsvFiles.length === 0) {
        if (errors.length > 0) {
          setError(`No valid files to upload. Issues found:\n${errors.join('\n')}`);
        } else {
          setError('No valid files found. Please select video or CSV files.');
        }
        // Clean up any created URLs
        validPreviews.forEach(url => {
          try {
            URL.revokeObjectURL(url);
          } catch (revokeError) {
            console.error('Error revoking URL:', revokeError);
          }
        });
        return;
      }

      // Show warnings for any errors but continue with valid files
      if (errors.length > 0) {
        console.warn('File upload warnings:', errors);
        // You might want to show warnings differently than errors
        // For now, we'll log them but continue processing valid files
      }

      // Process valid video files
      if (validVideoFiles.length > 0) {
        setVideoFiles(prevFiles => [...prevFiles, ...validVideoFiles]);
        setVideoPreviews(prevPreviews => [...prevPreviews, ...validPreviews]);
      }

      // Process valid CSV files
      if (validCsvFiles.length > 0) {
        try {
          const processedCsvFiles = validCsvFiles.map(file => ({
            id: Date.now() + Math.random(),
            file: file,
            name: file.name,
            type: 'csv',
            size: formatFileSizeHelper(file.size),
            progress: 0,
            status: 'ready',
            isOneDrive: false
          }));

          // Replace any existing CSV files instead of adding to the array
          setMyVideos(prevFiles => {
            // Filter out any existing CSV files
            const nonCsvFiles = prevFiles.filter(file => file.type !== 'csv');
            // Add the new CSV file
            return [...nonCsvFiles, ...processedCsvFiles];
          });

          // Set the current CSV file
          setCsvFile(processedCsvFiles[0]);
          successMessage(`Added CSV file: ${validCsvFiles[0].name}`);
        } catch (csvError) {
          console.error('Error processing CSV files:', csvError);
          setError(`Error processing CSV file: ${csvError.message}`);
          return;
        }
      }

      // Success handling
      if (validVideoFiles.length > 0 || validCsvFiles.length > 0) {
        setError(null);

        // Clear the input to allow re-uploading the same files if needed
        if (e.target) {
          e.target.value = '';
        }

        // Optional success message for videos
        if (validVideoFiles.length > 0 && errors.length === 0) {
          // successMessage(`Successfully added ${validVideoFiles.length} video file(s).`);
        }

        // Show warnings if there were any issues with some files
        if (errors.length > 0) {
          console.warn('Some files had issues but valid files were processed:', errors);
          // Optionally show a warning to user about skipped files
          // setWarning(`Some files were skipped: ${errors.join(', ')}`);
        }
      }

    } catch (globalError) {
      console.error('Unexpected error in file upload handler:', globalError);
      setError(`An unexpected error occurred while processing files: ${globalError.message}`);

      // Clean up any created object URLs in case of error
      if (validPreviews && validPreviews.length > 0) {
        validPreviews.forEach(url => {
          try {
            URL.revokeObjectURL(url);
          } catch (revokeError) {
            console.error('Error revoking URL:', revokeError);
          }
        });
      }
    }
  };

  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    // Create a synthetic event for the file handler
    const files = Array.from(e.dataTransfer.files);
    const syntheticEvent = {
      target: {
        files: files
      }
    };
    handleFileChange(syntheticEvent);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  // Remove a video from the list
  const handleRemoveVideo = (index) => {
    setVideoFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    setVideoPreviews(prevPreviews => {
      // Revoke the URL to prevent memory leaks
      URL.revokeObjectURL(prevPreviews[index]);
      return prevPreviews.filter((_, i) => i !== index);
    });
  };

  // YouTube URL input handler
  const handleYoutubeUrlChange = (e) => {
    setYoutubeUrl(e.target.value);
    // Clear error if user is typing a new URL
    if (error && error.includes('YouTube')) {
      setError(null);
    }
  };

  // Format file size helper
  const formatFileSizeHelper = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileType = (fileName) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();
    if (['csv', 'xlsx'].includes(extension)) return 'csv';
    if (['mp4', 'mov'].includes(extension)) return 'video';
    return 'other';
  };

  const getFileIcon = (type, isOneDrive = false) => {
    const iconProps = { className: "w-5 h-5 text-gray-600" };
    if (type?.includes('csv') || type?.includes('spreadsheet')) {
      return (
        <div className="relative">
          <FileSpreadsheet {...iconProps} className="w-5 h-5 text-green-600" />
          {isOneDrive && (
            <Cloud className="w-3 h-3 text-blue-500 absolute -top-1 -right-1" />
          )}
        </div>
      );
    }

    switch (type) {
      case 'csv':
        return (
          <div className="relative">
            <FileSpreadsheet className="w-8 h-8 text-green-600" />
            {isOneDrive && (
              <Cloud className="w-4 h-4 text-blue-500 absolute -top-1 -right-1" />
            )}
          </div>
        );
      case 'video':
        return (
          <div className="relative">
            <Play className="w-8 h-8 text-blue-600" />
            {isOneDrive && (
              <Cloud className="w-4 h-4 text-blue-500 absolute -top-1 -right-1" />
            )}
          </div>
        );
      default:
        return (
          <div className="relative">
            <FileText className="w-8 h-8 text-green-600" />
            {isOneDrive && (
              <Cloud className="w-4 h-4 text-blue-500 absolute -top-1 -right-1" />
            )}
          </div>
        );
    }
  };

  const validateFiles = (files) => {
    const csvFiles = files?.filter(file => getFileType(file?.name) === 'csv');
    const videoFiles = files?.filter(file => getFileType(file?.name) === 'video');
    const invalidFiles = files?.filter(file => !['csv', 'video'].includes(getFileType(file?.name)));

    if (invalidFiles?.length > 0) {
      return 'Only CSV and video files (.mp4, .mov) are allowed';
    }

    // Check if we already have a CSV file in myVideos
    const existingCsvFile = myVideos.find(file => file.type === 'csv');

    // Check if there are multiple CSV files
    if (csvFiles?.length > 1) {
      return 'Only one CSV file is allowed. Please select only one CSV file.';
    }

    // Check if there's already a CSV file and the user is trying to add another
    if (existingCsvFile && csvFiles?.length > 0) {
      return 'Only one CSV file is allowed. Please remove the existing CSV file first.';
    }

    // No restrictions on number of files - user can select as many as they want
    if (csvFiles?.length === 0 && videoFiles?.length === 0) {
      return 'Please select at least one CSV or video file';
    }

    return null;
  };

  const handleMyVideosDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleMyVideosDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    const files = Array.from(e?.dataTransfer?.files || []);
    handleMyVideosFiles(files);
  };

  const handleMyVideosFileInput = (e) => {
    const files = Array.from(e?.target?.files || []);
    handleMyVideosFiles(files);
  };

  // Reference to the OneDrive picker button
  const oneDrivePickerRef = useRef(null);

  // Function to programmatically open OneDrive picker
  const openOneDrivePicker = () => {
    if (oneDrivePickerRef.current) {
      oneDrivePickerRef.current.click();
    }
  };

  const handleMyVideosFiles = (files) => {
    setMyVideosErrorMessage('');

    const validationError = validateFiles(files);
    if (validationError) {
      setMyVideosErrorMessage(validationError);
      return;
    }

    // Process files while ensuring only one CSV
    const csvFiles = [];
    const videoFiles = [];

    // First pass - separate files by type
    files.forEach(file => {
      const fileType = getFileType(file?.name);
      if (fileType === 'csv' && csvFiles.length === 0) {
        csvFiles.push(file);
      } else if (fileType === 'video') {
        videoFiles.push(file);
      }
    });

    // Create file objects
    const newFiles = [];

    // Add video files
    videoFiles.forEach(file => {
      newFiles.push({
        id: Date.now() + Math.random(),
        file: file,
        name: file?.name,
        type: 'video',
        size: formatFileSizeHelper(file?.size),
        progress: 0,
        status: 'ready',
        isOneDrive: false,
        csv_id: null
      });
    });

    // Add CSV file (if any)
    if (csvFiles.length > 0) {
      newFiles.push({
        id: Date.now() + Math.random(),
        file: csvFiles[0],
        name: csvFiles[0]?.name,
        type: 'csv',
        size: formatFileSizeHelper(csvFiles[0]?.size),
        progress: 0,
        status: 'ready',
        isOneDrive: false,
        csv_id: null
      });
    }

    // Update myVideos array - replace existing CSV if there is one
    setMyVideos(prevFiles => {
      // Filter out any existing CSV files if we're adding a new one
      const filteredFiles = csvFiles.length > 0
        ? prevFiles.filter(file => file.type !== 'csv')
        : prevFiles;
      return [...filteredFiles, ...newFiles];
    });

    // Update UI references for compatibility with existing code
    const newCsvFiles = newFiles.filter(file => file.type === 'csv');
    const newVideoFiles = newFiles.filter(file => file.type === 'video');

    if (newCsvFiles.length > 0) {
      setCsvFile(newCsvFiles[0]);
      successMessage(`Added CSV file: ${newCsvFiles[0].name}`);
    }

    if (newVideoFiles.length > 0) {
      setMyVideoFile(newVideoFiles[0]);
      if (newVideoFiles.length === 1) {
        successMessage(`Added video file: ${newVideoFiles[0].name}`);
      } else {
        successMessage(`Added ${newVideoFiles.length} video files`);
      }
    }
  };

  // Handle OneDrive file selection
  const handleOneDriveSelect = (data) => {
    console.log('OneDrive selection data:', data)
    if (data?.action === 'cancel') {
      return;
    }

    if (data?.action === 'error') {
      setError(data.message || 'Error connecting to OneDrive');
      return;
    }

    if (data?.docs) {
      // Apply validation logic as normal file upload
      const selectedOneDriveFiles = data?.docs;

      // Process OneDrive files based on the current input type
      if (inputType === 'upload') {
        // Filter video and CSV files
        const oneDriveVideoFiles = [];
        const oneDriveCsvFiles = [];

        // Check if we already have a CSV file
        const existingCsvFile = myVideos.find(file => file.type === 'csv');

        selectedOneDriveFiles.forEach(doc => {
          const extension = doc.name.split('.').pop().toLowerCase();
          if (['mp4', 'mov', 'avi'].includes(extension)) {
            oneDriveVideoFiles.push(doc);
          } else if (extension === 'csv') {
            // Only add the first CSV file if we don't already have one
            if (!existingCsvFile && oneDriveCsvFiles.length === 0) {
              oneDriveCsvFiles.push(doc);
            } else if (existingCsvFile) {
              setError('Only one CSV file is allowed. Please remove the existing CSV file first.');
              // Continue processing other files
            } else if (oneDriveCsvFiles.length > 0) {
              setError('Only one CSV file is allowed. Only the first CSV file will be used.');
              // Continue processing other files
            }
          }
        });

        if (oneDriveVideoFiles.length === 0 && oneDriveCsvFiles.length === 0) {
          setError('Please select video files (MP4, MOV, AVI) or CSV files from OneDrive');
          return;
        }

        // Process CSV files first
        if (oneDriveCsvFiles.length > 0) {
          // Process the CSV files
          const processedCsvFiles = oneDriveCsvFiles.map(doc => ({
            id: Date.now() + Math.random(),
            file: {
              name: doc.name,
              size: doc.size || 0,
              // Add OneDrive specific properties
              oneDriveId: doc.id,
              webUrl: doc.webUrl,
              mimeType: 'text/csv'
            },
            name: doc.name,
            type: 'csv',
            size: formatFileSizeHelper(doc.size || 0),
            progress: 0,
            status: 'ready',
            isOneDrive: true // Flag to identify OneDrive files
          }));

          // Replace any existing CSV files instead of adding to the array
          setMyVideos(prevFiles => {
            // Filter out any existing CSV files
            const nonCsvFiles = prevFiles.filter(file => file.type !== 'csv');
            // Add the new CSV file
            return [...nonCsvFiles, ...processedCsvFiles];
          });

          // For compatibility with existing code
          if (processedCsvFiles.length > 0) {
            setCsvFile(processedCsvFiles[0]);
            successMessage(`Selected CSV file: ${processedCsvFiles[0].name} from OneDrive`);
          }
        }

        // Process video files
        if (oneDriveVideoFiles.length > 0) {
          // Process OneDrive video files in parallel to improve performance
          const processOneDriveFiles = async () => {
            setIsDownloadingOneDriveFiles(true);
            const totalFiles = oneDriveVideoFiles.length;
            let completedFiles = 0;

            // Initialize download status for each file
            const downloadStatus = {};
            oneDriveVideoFiles.forEach(doc => {
              downloadStatus[doc.id] = {
                progress: 0,
                status: 'pending',
                name: doc.name
              };
            });
            setOneDriveDownloads(downloadStatus);

            // Function to download a single file
            const downloadFile = async (doc) => {
              try {
                // Update download status
                setOneDriveDownloads(prev => ({
                  ...prev,
                  [doc.id]: { ...prev[doc.id], status: 'downloading', progress: 10 }
                }));

                // Show loading message
                successMessage(`Downloading ${doc.name} from OneDrive...`);

                // Actually download the file content from OneDrive
                const blob = await oneDriveService.downloadFile(doc.id);

                if (!blob) {
                  throw new Error(`Failed to download ${doc.name} from OneDrive`);
                }

                // Update progress
                setOneDriveDownloads(prev => ({
                  ...prev,
                  [doc.id]: { ...prev[doc.id], progress: 50 }
                }));

                // Create a real file object with the downloaded content
                const realFile = new File([blob], doc.name, {
                  type: doc.mimeType || 'video/mp4'
                });

                // Add isOneDrive flag and original ID to identify OneDrive files
                realFile.isOneDrive = true;
                realFile.oneDriveId = doc.id;

                // Add to video files list
                setVideoFiles(prevFiles => [...prevFiles, realFile]);

                // Create a real preview URL from the actual file content
                const previewUrl = URL.createObjectURL(blob);
                setVideoPreviews(prevPreviews => [...prevPreviews, previewUrl]);

                // Update status to completed
                setOneDriveDownloads(prev => ({
                  ...prev,
                  [doc.id]: { ...prev[doc.id], status: 'completed', progress: 100 }
                }));

                // Update completed count
                completedFiles++;

                // Inform the user of success
                successMessage(`Successfully loaded ${doc.name} from OneDrive (${completedFiles}/${totalFiles})`);

                return { success: true, docId: doc.id };
              } catch (error) {
                console.error('Error processing OneDrive file:', error);
                setError(`Error processing ${doc.name}: ${error.message}`);

                // Update status to failed
                setOneDriveDownloads(prev => ({
                  ...prev,
                  [doc.id]: { ...prev[doc.id], status: 'failed', progress: 0 }
                }));

                return { success: false, docId: doc.id, error };
              }
            };

            // Set up a limit for parallel downloads (typically 3-5 is a good balance)
            const MAX_CONCURRENT_DOWNLOADS = 3;

            // Process files in batches for controlled parallelism
            for (let i = 0; i < oneDriveVideoFiles.length; i += MAX_CONCURRENT_DOWNLOADS) {
              const batch = oneDriveVideoFiles.slice(i, i + MAX_CONCURRENT_DOWNLOADS);

              // Process this batch in parallel
              const results = await Promise.all(batch.map(doc => downloadFile(doc)));

              // Log results of this batch
              console.log(`Completed batch ${Math.floor(i / MAX_CONCURRENT_DOWNLOADS) + 1} of OneDrive downloads:`,
                results.map(r => r.success ? 'Success' : 'Failed'));
            }

            setIsDownloadingOneDriveFiles(false);
          };

          // Start processing files
          processOneDriveFiles();
        }
      } else {
        // For My Videos tab functionality (keeping this for backward compatibility)
        // Convert OneDrive files to the same format as normal files for validation
        const mockFiles = selectedOneDriveFiles?.map(doc => ({
          name: doc?.name,
          size: doc?.size || 0,
          type: getFileType(doc?.name)
        }));

        // Use the existing validation function
        const validationError = validateFiles(mockFiles);
        if (validationError) {
          setMyVideosErrorMessage(validationError);
          return;
        }

        // Clear any existing error messages
        setMyVideosErrorMessage('');

        // Process the valid OneDrive files
        const processedFiles = selectedOneDriveFiles?.map(doc => ({
          id: Date.now() + Math.random(),
          file: {
            name: doc?.name,
            size: doc?.size || 0,
            // Add OneDrive specific properties
            oneDriveId: doc?.id,
            webUrl: doc?.webUrl,
            mimeType: doc?.mimeType
          },
          name: doc?.name,
          type: getFileType(doc?.name),
          size: formatFileSizeHelper(doc?.size || 0),
          progress: 0,
          status: 'ready',
          isOneDrive: true // Flag to identify OneDrive files
        }));

        // Add all selected files to myVideos array
        setMyVideos(prevFiles => [...prevFiles, ...processedFiles]);

        // For compatibility with existing code, also update csvFile and myVideoFile
        // with the first files of each type
        const csvFiles = processedFiles.filter(file => file.type === 'csv');
        const videoFiles = processedFiles.filter(file => file.type === 'video');

        if (csvFiles.length > 0) {
          setCsvFile(csvFiles[0]);
        }

        if (videoFiles.length > 0) {
          setMyVideoFile(videoFiles[0]);
        }
      }

      // Save token and log selection
      let oneDriveToken = sessionStorage?.getItem('oneDriveToken');
      let obj = {
        user_id: getUser()?.id,
        files: data?.docs,
        oneDriveToken,
      };
      console.log('OneDrive files selected:', obj);
    }
  };

  // Define accepted file types for OneDrive
  const acceptedFileTypes = ['csv', 'mp4', 'mov'];

  const removeMyVideosFile = (name) => {
    // Remove from the myVideos array
    setMyVideos(prev => prev.filter(file => file.name !== name));

    // Also update the single file references for backward compatibility
    if (csvFile?.name === name) {
      setCsvFile(null);
    } else if (myVideoFile?.name === name) {
      setMyVideoFile(null);
    }

    setMyVideosErrorMessage('');
  };

  // Download file from OneDrive
  const downloadOneDriveFile = async (fileId) => {
    try {
      if (!oneDriveService) {
        // Try to initialize OneDrive service if not available
        if (instance && isInitialized) {
          console.log('Reinitializing OneDrive service for download');
          const service = new OneDriveService(instance);
          setOneDriveService(service);

          // Wait for the service to be set
          await new Promise(resolve => setTimeout(resolve, 100));

          // Verify we have a valid token
          const token = await service.getTokenSilently();
          if (!token) {
            throw new Error('Failed to get OneDrive access token. Please try reconnecting to OneDrive.');
          }

          // Use the new service
          const blob = await service.downloadFile(fileId);
          if (!blob) {
            throw new Error('Failed to download file from OneDrive');
          }
          return blob;
        } else {
          throw new Error('OneDrive service not initialized');
        }
      }

      // Use the oneDriveService to download the file
      const blob = await oneDriveService.downloadFile(fileId);

      if (!blob) {
        throw new Error('Failed to download file from OneDrive');
      }

      return blob;
    } catch (error) {
      console.error('Error downloading from OneDrive:', error);

      // Check for authorization errors
      if (error.message?.includes('401') ||
        error.message?.includes('unauthorized') ||
        error.message?.includes('access token')) {
        // Try to refresh token and retry
        try {
          if (instance) {
            await forceNewLogin();
            const service = new OneDriveService(instance);
            setOneDriveService(service);

            // Wait a moment for the service to be set
            await new Promise(resolve => setTimeout(resolve, 300));

            // Try again with new token
            return await service.downloadFile(fileId);
          }
        } catch (retryError) {
          console.error('Retry failed:', retryError);
          throw new Error('Authorization failed. Please reconnect to OneDrive and try again.');
        }
      }

      throw error;
    }
  };

  // Clear current input and results
  const resetForm = () => {
    if (inputType === 'upload') {
      setVideoFiles([]);
      setVideoPreviews([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      setYoutubeUrl('');
    }
    setAdditionalInstructions('');
    setAnalysisResult('');
    setActionsVisible(false);
    setAudioUrl(null);
    setNarrationText(null);
    setShowNarrationText(false);
    setPdfUrl(null);
    setError(null);
    setAnalysisTitle('');
    setPlayerIdForAnalysis('');
    setSelectedAnalysisId(null);
    setAthleticPowerMetrics(null);
    setShowAthleticPowerMetrics(false);
    // Reset dropdown folder but not the table folder
    setDropdownFolderId(null);

    // Important: Do NOT reset CSV files as they may be needed for subsequent uploads
    // We preserve myVideos that contain CSV files with their csv_id for reuse
  };

  const fetchPlayers = async () => {
    try {
      const savedTeam = sessionStorage.getItem('selectedTeam');
      const team = savedTeam ? JSON.parse(savedTeam) : null;

      if (!team?.team_id && !team?.name && !team?.team_name) {
        console.warn('No team selected');
        return;
      }

      const sport = getSelectedSport();
      const teamName = team?.name || team?.team_name || team?.full_name;
      let response;

      // Use college rosters endpoint for basketball sports (MBB, WBB, CBB)
      // This endpoint uses team_name search which is more reliable
      if (['MBB', 'WBB', 'CBB'].includes(sport)) {
        response = await getMBBRoasters(teamName);
      } else {
        // For other sports (MLB, NBA, etc.), try path-based endpoint first
        // Silently handle 404s — player list is optional context for video analysis
        try {
          response = await apiClient.get(`${sport}/teams/${team.team_id}/players`);
        } catch (pathError) {
          if (pathError?.response?.status === 404) {
            // Endpoint doesn't exist for this sport/team — not an error, just no data
            console.info(`[VideoAnalytics] No players endpoint for ${sport}/${team.team_id}, skipping`);
            return;
          }
          // Fallback to query parameter endpoint
          try {
            response = await apiClient.get(`${sport}/players/`, {
              params: {
                team_id: team.team_id,
                skip: 0,
                limit: 100
              }
            });
          } catch (fallbackError) {
            if (fallbackError?.response?.status === 404) {
              console.info(`[VideoAnalytics] No players fallback for ${sport}, skipping`);
              return;
            }
            throw fallbackError;
          }
        }
      }
      setAllPlayers(response?.data || []);
    } catch (error) {
      console.error('Error fetching players:', error);
      setAllPlayers([]);
    }
  };

  const getPlayerName = (playerId) => {
    const player = allPlayers.find(p => p.PlayerID === playerId);
    return player ? player.Player : 'Unknown Player';
  };

  return (
    <div
      style={{ background: C.bg, minHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto', color: C.text }}
      className="rounded-lg overflow-hidden"
      data-theme="dark"
    >
      {/* GM Shell Header */}
      <div style={{ padding: '20px 20px 16px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: C.gold }} />
          <span style={{ fontSize: 9, fontWeight: 600, color: C.gold, letterSpacing: 3, textTransform: 'uppercase' }}>
            Video Analytics
          </span>
        </div>
        <div style={{ fontSize: 22, fontWeight: 200, letterSpacing: -0.5, lineHeight: 1.2, color: C.text }}>
          {sportName ? `${sportName} Film Room` : 'Film Room'}
        </div>
        <div style={{ fontSize: 11, color: C.t2, marginTop: 4 }}>Upload · Analyze · Organize</div>
      </div>

      {/* Analysis Mode Tab Switcher - Glassmorphism */}
      <div
        className="border-b border-[#1A1A1A]"
        style={{
          background: 'rgba(13, 13, 18, 0.8)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)'
        }}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-2">
            {/* Video Analysis - Main Tab (unified: single video = simple, multiple = MoE) */}
            <button
              onClick={() => setAnalysisTab('analysis')}
              className={`flex items-center px-5 py-2.5 rounded-xl font-medium transition-all duration-200 ${
                analysisTab === 'analysis'
                  ? 'text-black shadow-lg'
                  : 'text-[#999999] hover:text-[#F5F5F5] hover:bg-[rgba(255,255,255,0.04)]'
              }`}
              style={analysisTab === 'analysis' ? {
                background: C.gold,
                boxShadow: '0 0 20px rgba(232, 184, 0, 0.4)'
              } : {}}
            >
              <Video size={18} className="mr-2" />
              Video Analysis
            </button>
            {/* My Videos - Folder Management */}
            <button
              onClick={() => setAnalysisTab('myvideos')}
              className={`flex items-center px-5 py-2.5 rounded-xl font-medium transition-all duration-200 ${
                analysisTab === 'myvideos'
                  ? 'text-black shadow-lg'
                  : 'text-[#999999] hover:text-[#F5F5F5] hover:bg-[rgba(255,255,255,0.04)]'
              }`}
              style={analysisTab === 'myvideos' ? {
                background: C.gold,
                boxShadow: '0 0 20px rgba(232, 184, 0, 0.3)'
              } : {}}
            >
              <Folder size={18} className="mr-2" />
              My Videos
            </button>
          </div>
          {analysisTab === 'analysis' && (
            <div className="text-xs text-[#555555]">
              Drop Videos • Prompt or Template • Smart Analysis
            </div>
          )}
          {analysisTab === 'myvideos' && (
            <div className="text-xs text-[#555555]">
              Browse Folders • View Previous Analyses
            </div>
          )}
        </div>
      </div>

      {/* Error/Success message display */}
      {error && (
        typeof error === 'string' && isYouTubeError(error) ? (
          <div className="mx-4 mt-3">
            <YouTubeErrorDisplay
              errorMessage={error}
              onDismiss={() => setError(null)}
              onUploadClick={() => setInputType('upload')}
              showUploadButton={true}
            />
          </div>
        ) : (
          <div className={`mx-4 mt-3 p-3 border rounded flex items-start ${error.type === 'success'
            ? 'bg-green-900 border-green-700 text-green-200'
            : 'bg-red-900 border-red-700 text-red-200'
            }`}>
            {error.type === 'success'
              ? <CheckCircle size={24} className="mr-3 mt-0.5 flex-shrink-0 text-green-300" />
              : <AlertTriangle size={24} className="mr-3 mt-0.5 flex-shrink-0" />
            }
            <div>
              {typeof error === 'string'
                ? error.split('\n\n').map((paragraph, index) => (
                  <p key={index} className={index > 0 ? 'mt-2 text-sm' : 'font-semibold'}>
                    {paragraph}
                  </p>
                ))
                : <p className="font-semibold">{error.message}</p>
              }
            </div>
          </div>
        )
      )}

      {/* Conditional Content Based on Analysis Tab */}
      {analysisTab === 'analysis' ? (
        /* Unified Video Analyzer - handles both single and multi-video */
        <div style={{ flex: 1 }} className="overflow-auto">
          <VideoAnalyzer
            className="h-full"
            onAnalysisComplete={(result) => {
              console.log('Video Analysis complete:', result);
            }}
          />
        </div>
      ) : analysisTab === 'myvideos' ? (
        /* My Videos - Folder Management View */
        <div style={{ flex: 1 }} className="overflow-auto p-4">
          {!selectedFolderId ? (
            /* Folder List View */
            <div
              className="rounded-[14px] overflow-hidden border border-[#1A1A1A]"
              style={{
                background: 'rgba(13, 13, 18, 0.6)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
              }}
            >
              {/* Header */}
              <div
                className="p-5 flex justify-between items-center border-b border-[#1A1A1A]"
              >
                <div>
                  <h3 className="text-xl font-semibold" style={{ color: C.gold }}>My Video Folders</h3>
                  <p className="text-sm mt-1 text-[#999999]">{allFolders.length || 0} folders found</p>
                </div>
                <button
                  onClick={() => fetchAllFolders()}
                  className="flex items-center px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 border border-[#1A1A1A] text-[#F5F5F5] hover:bg-[rgba(255,255,255,0.04)]"
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)'
                  }}
                >
                  <RefreshCw size={16} className="mr-2" />
                  Refresh
                </button>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#1A1A1A]">
                      <th className="text-left p-4 font-medium text-sm text-[#999999]">FOLDER NAME</th>
                      <th className="text-left p-4 font-medium text-sm text-[#999999]">CREATED AT</th>
                      <th className="text-left p-4 font-medium text-sm text-[#999999]">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isFolderLoading ? (
                      <tr>
                        <td colSpan="3" className="text-center py-12">
                          <Loader2 size={20} className="mx-auto mb-4 animate-spin" style={{ color: C.gold }} />
                          <p className="text-[#999999]">Loading folders...</p>
                        </td>
                      </tr>
                    ) : allFolders.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="text-center py-12">
                          <Folder className="mx-auto w-12 h-12 mb-4" style={{ color: C.t2 }} />
                          <p className={'text-gray-400'}>No folders found</p>
                          <p className={`text-sm ${'text-gray-500'}`}>Create a folder when analyzing videos</p>
                        </td>
                      </tr>
                    ) : (
                      allFolders.map((folder) => (
                        <tr
                          key={folder.id}
                          className={`transition-all duration-200 cursor-pointer ${'border-b border-white/5 hover:bg-white/5'}`}
                          onClick={() => handleFolderClick(folder.id, folder.name)}
                        >
                          <td className="p-4">
                            <div className="flex items-center">
                              <Folder className="w-5 h-5 mr-2" style={{ color: C.goldMuted }} />
                              <span className={`font-medium ${'text-white'}`}>
                                {folder.name}
                              </span>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center">
                              <Calendar className={`w-4 h-4 mr-2 ${'text-gray-500'}`} />
                              <span className={'text-gray-300'}>
                                {new Date(folder.created_at).toLocaleDateString()}
                              </span>
                              <span className={`text-xs ml-2 ${'text-gray-500'}`}>
                                {new Date(folder.created_at).toLocaleTimeString()}
                              </span>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex space-x-2">
                              <button
                                className={`flex items-center px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 ${'border border-white/10 text-white hover:bg-white/5'}`}
                                style={{ background: 'rgba(255, 255, 255, 0.03)' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFolderClick(folder.id, folder.name);
                                }}
                              >
                                <ExternalLink size={12} className="mr-1" />
                                Open
                              </button>
                              <ConfirmDialog
                                title="Delete Folder"
                                description="Are you sure you want to delete this folder and all its videos?"
                                onConfirm={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFolder(folder.id);
                                }}
                                okText="Yes"
                                cancelText="No"
                                onCancel={(e) => e.stopPropagation()}
                                destructive
                              >
                                <button
                                  className={`flex items-center px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 ${'border border-red-500/30 text-red-400 hover:bg-red-500/10'}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Trash2 size={12} className="mr-1" />
                                  Delete
                                </button>
                              </ConfirmDialog>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Folder Contents View */
            <div>
              {/* Back to Folders Button */}
              <div className="mb-4">
                <button
                  onClick={() => {
                    setSelectedFolderId(null);
                    navigate('');
                    setExistingVideos([]);
                    setDropdownFolderId(null);
                  }}
                  className={`px-5 py-2.5 rounded-xl flex items-center font-medium transition-all duration-300 ${'border border-white/10 text-white hover:bg-white/5'}`}
                  style={{ background: 'rgba(255, 255, 255, 0.03)' }}
                >
                  <ChevronLeft size={18} className="mr-2" style={{ color: C.t2 }} />
                  Back to Folders
                </button>
              </div>
              <VideoAnalyticsTable
                onRefresh={fetchExistingVideos}
                handlePageChange={handlePageChange}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                totalRecords={totalRecords}
                existingVideos={existingVideos}
                setExistingVideos={setExistingVideos}
                selectedFolderId={selectedFolderId}
                allFolders={allFolders}
                serIsShowCsv={serIsShowCsv}
                isLoading={isTableLoading}
              />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default VideoAnalytics;